import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MyRideParticipation, RsvpStatus } from '@ridemaadi/types';
import { apiFetch } from '../../lib/api-client';
import { rideKeys } from './hooks';

export const participantsApi = {
  list: (rideId: string) =>
    apiFetch<{ items: import('@ridemaadi/types').RideParticipant[] }>(`/rides/${rideId}/participants`),
  myRsvp: (rideId: string) =>
    apiFetch<MyRideParticipation>(`/rides/${rideId}/my-rsvp`),
  rsvp: (rideId: string, rsvpStatus: RsvpStatus, motorcycleId?: string) =>
    apiFetch<import('@ridemaadi/types').RideParticipant>(`/rides/${rideId}/rsvp`, {
      method: 'POST',
      body: { rsvpStatus, motorcycleId },
    }),
  leave: (rideId: string) => apiFetch<void>(`/rides/${rideId}/rsvp`, { method: 'DELETE' }),
};

export function useParticipants(rideId: string | undefined) {
  return useQuery({
    queryKey: ['rides', 'participants', rideId ?? ''],
    queryFn: () => participantsApi.list(rideId!),
    enabled: Boolean(rideId),
  });
}

export function useMyRsvp(rideId: string | undefined) {
  return useQuery({
    queryKey: ['rides', 'my-rsvp', rideId ?? ''],
    queryFn: () => participantsApi.myRsvp(rideId!),
    enabled: Boolean(rideId),
  });
}

function useInvalidateRide(rideId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['rides', 'participants', rideId] });
    qc.invalidateQueries({ queryKey: ['rides', 'my-rsvp', rideId] });
    qc.invalidateQueries({ queryKey: rideKeys.detail(rideId) });
    qc.invalidateQueries({ queryKey: rideKeys.mine });
  };
}

export function useRsvp(rideId: string) {
  const invalidate = useInvalidateRide(rideId);
  return useMutation({
    mutationFn: ({ status, motorcycleId }: { status: RsvpStatus; motorcycleId?: string }) =>
      participantsApi.rsvp(rideId, status, motorcycleId),
    onSuccess: invalidate,
  });
}

export function useLeaveRide(rideId: string) {
  const invalidate = useInvalidateRide(rideId);
  return useMutation({ mutationFn: () => participantsApi.leave(rideId), onSuccess: invalidate });
}
